import * as React from 'react';

import {
  Tab as MuiTab,
  Tabs as MuiTabs,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import Box from '@mui/material/Box';
import { Stack, styled } from '@mui/material';
import { useDispatch, useSelector } from 'react-redux';
import {
  currentGalleryCarIdSet,
  getAllGalleryImagesLow,
  getAllGalleryImagesMid,
  getCurrentGalleryCarId,
  getGalleryZoomStep,
} from '../../store/gallery-slice';
import ImageListItem from './image-list-item';
import InteriorIcon from '../../assets/icon/interior-icon';
import ExteriorIcon from '../../assets/icon/exterior-icon';
import constants from '../../config/constants';
import GenerateImageListData from './generate-image-list-data';
import { galleryTabSet, getGalleryTab } from '../../store/car-slice';

interface TabPanelProps {
  children?: React.ReactNode;
  currenttab: string;
  gallerytab: string;
}

const MobilePortraitTabs = styled(MuiTabs)(({ theme }) => ({
  '.MuiTabs-flexContainer': {
    display: 'grid',
    justifyContent: 'center',
  },
  '.MuiTabs-indicator': {
    backgroundColor: 'transparent',
  },
  '.MuiTabs-scroller>.MuiTabs-flexContainer>button>svg': {
    marginRight: 0,
  },
  alignItems: 'center',
  bottom: 0,
  [`${theme.breakpoints.up('xl')}`]: {
    height: '230px',
    width: '150px',
  },
  [`${theme.breakpoints.between('lg', 'xl')}`]: {
    height: '200px',
    width: '150px',
  },
  [`${theme.breakpoints.between('md', 'lg')}`]: {
    height: '150px',
    width: '150px',
  },
  [`${theme.breakpoints.between('sm', 'md')}`]: {
    height: '100px',
    width: '150px',
  },
  [`${theme.breakpoints.between('xs', 'sm')}`]: {
    height: '100px',
    width: '100px',
  },
  [`${theme.breakpoints.down('xs')}`]: {
    height: '100px',
    width: '100px',
  },
  zIndex: 1,
}));

const MobileTab = styled(MuiTab)<any>(({ theme }) => ({
  borderRadius: theme.spacing(2),
  [theme.breakpoints.down('sm')]: {
    scale: '.9',
  },
  [theme.breakpoints.down('xs')]: {
    scale: '.8',
  },
  [theme.breakpoints.down('lg')]: {
    display: 'flex',
    fontSize: '10px',
    fontWeight: 700,
    flexDirection: 'column',
    '&>svg': {
      marginRight: '0 !important',
      marginBottom: 4,
    },
    height: 'auto',
    justifyContent: 'center',
    alignItems: 'center',
    marginInline: 'auto',
    minHeight: '50px !important',
    minWidth: '50px',
    padding: '0 !important',
  },
  [theme.breakpoints.between('md', 'lg')]: {
    display: 'flex',
    fontWeight: 700,
    height: 'auto',
    justifyContent: 'center',
    marginInline: 'auto',
  },
  fontWeight: 700,
  height: '90px',
  justifyContent: 'space-around',
  marginInline: 'auto',
  textTransform: 'capitalize',
}));

const Tabs = styled(MuiTabs)(({ theme }) => ({
  [theme.breakpoints.down('md')]: {
    height: 40,
    padding: '40px 15px',
  },
  '.MuiTabs-indicator': {
    backgroundColor: 'transparent',
  },
  alignItems: 'center',
  height: 80,
  '.MuiTabs-scroller': {
    background: '#252525',
    borderRadius: theme.spacing(1.5),
  },
}));

const Tab = styled(MuiTab)(({ theme }) => ({
  [theme.breakpoints.down('md')]: {
    fontSize: '15px',
  },
  height: 50,
  minHeight: 40,
  fontWeight: 700,
  marginInline: 'auto',
  textTransform: 'capitalize',
  borderRadius: theme.spacing(1.5),
}));

const CombinedTabs = ({ orientation, ...props }: any) => {
  if (orientation === 'portrait') {
    return <MobilePortraitTabs {...props} />;
  } else {
    return <Tabs {...props} />;
  }
};

const CombinedTab = ({ device, ...props }: any) => {
  if (device === 'mobile') {
    return <MobileTab {...props} />;
  } else {
    return <Tab {...props} />;
  }
};

function CustomTabPanel(props: TabPanelProps) {
  const isPortrait = useMediaQuery('(orientation: portrait)');
  const isMobile = useMediaQuery((theme: any) => theme.breakpoints.down('lg'));
  const isMediumDown = useMediaQuery((theme: any) =>
    theme.breakpoints.down('md'),
  );

  const { children, gallerytab, currenttab, ...other } = props;

  return (
    <div
      aria-labelledby={`simple-tab-${currenttab}`}
      hidden={gallerytab !== currenttab}
      id={`simple-tabpanel-${currenttab}`}
      role="tabpanel"
      style={{
        backgroundColor: 'rgba(30, 30, 30, 0.5)',
        width:
          isPortrait && isMediumDown
            ? '80%'
            : isPortrait && isMobile
              ? '90%'
              : '100%',
        height:
          isPortrait && isMobile
            ? '100%'
            : `calc(100% - ${isMobile ? 50 : 80}px)`,
        overflow: isMobile ? 'auto' : '',
        display: gallerytab === currenttab ? 'block' : 'none',
      }}
      {...other}
    >
      {children}
    </div>
  );
}

function a11yProps(index: number) {
  return {
    id: `simple-tab-${index}`,
    'aria-controls': `simple-tabpanel-${index}`,
  };
}

export default function ImageList() {
  const { palette }: any = useTheme();
  const dispatch = useDispatch();
  const galleryTab = useSelector(getGalleryTab);

  // Selectors
  const currentGalleryCarId = useSelector(getCurrentGalleryCarId);

  const zoomStep = useSelector(getGalleryZoomStep);
  // low quality gallery images
  const { interiorGalleryLow, exteriorGalleryLow } = useSelector(
    getAllGalleryImagesLow,
  );

  // mid quality gallery images
  const { interiorGalleryMid, exteriorGalleryMid } = useSelector(
    getAllGalleryImagesMid,
  );

  // Media Queries
  const isMobile = useMediaQuery((theme: any) => theme.breakpoints.down('lg'));
  const isMediumDown = useMediaQuery((theme: any) =>
    theme.breakpoints.down('md'),
  );

  const isPortrait = useMediaQuery('(orientation: portrait)');

  /**
   * change quality by width
   */
  const galleryData = GenerateImageListData({
    interiorGalleryMid,
    exteriorGalleryMid,
    interiorGalleryLow,
    exteriorGalleryLow,
  });

  const handleChange = (event: React.SyntheticEvent, tabValue: string) => {
    const FirstExteriorId = galleryData[0]?.gallery[0]?.id;
    const FirstInteriorId = galleryData[1]?.gallery[0]?.id;

    if (
      tabValue === 'gallery-exterior' &&
      currentGalleryCarId !== FirstExteriorId
    ) {
      dispatch(currentGalleryCarIdSet(FirstExteriorId));
    } else if (
      tabValue === 'gallery-interior' &&
      currentGalleryCarId !== FirstInteriorId
    ) {
      dispatch(currentGalleryCarIdSet(FirstInteriorId));
    }

    dispatch(galleryTabSet(tabValue));
  };

  React.useEffect(() => {
    if (interiorGalleryLow.length === 0 || interiorGalleryMid.length === 0) {
      dispatch(galleryTabSet('gallery-exterior'));
    }
  }, []);

  React.useEffect(() => {
    if (interiorGalleryLow.length === 0 || interiorGalleryMid.length === 0)
      return;

    if (exteriorGalleryLow.length === 0 || exteriorGalleryMid.length === 0)
      return;

    if (currentGalleryCarId.includes('exterior')) {
      dispatch(galleryTabSet('gallery-exterior'));
    } else {
      dispatch(galleryTabSet('gallery-interior'));
    }
  }, [currentGalleryCarId]);

  const mountRef: any = React.useRef(false);

  return (
    <Stack
      className={isMobile ? '' : 'animate-slide-right'}
      sx={{
        position: 'relative',
        transition: 'all .3s ease-in-out',
        width: isPortrait && isMobile ? '100%' : isMediumDown ? '30%' : '400px',
        minWidth:
          isPortrait && isMobile ? '100%' : isMediumDown ? '30%' : '400px',
        height: isMobile && isPortrait ? '20%' : '100%',
        display: zoomStep === 2 ? 'none' : isMobile && isPortrait ? 'flex' : '',
        justifyContent: isMobile && isPortrait ? 'center' : '',
        alignItems: isMobile && isPortrait ? 'center' : '',
        flexDirection: isMobile && isPortrait ? 'row' : '',
      }}
    >
      <Box
        sx={{
          position: isPortrait && isMobile ? '' : 'relative',
          left: 0,
          width:
            isPortrait && isMediumDown
              ? '20%'
              : isPortrait && isMobile
                ? '10%'
                : '100%',
          height: isMobile && isPortrait ? '100%' : 'auto',
          margin: '0 auto',
          display: 'flex',
          flexDirection: 'column',
          flexFlow: 'wrap',
          backgroundColor: '#292929',
          borderColor: 'divider',
          justifyContent: 'center',
          alignItems: 'center',
          background: 'rgba( 0, 0, 0, 0.70 )',
          backdropFilter: constants.BLUR_STYLE.filter,
        }}
      >
        <CombinedTabs
          aria-label="basic tabs example"
          onChange={handleChange}
          orientation={isPortrait ? 'portrait' : 'landscape'}
          value={galleryTab}
        >
          {exteriorGalleryMid.length > 0 && (
            <CombinedTab
              device={isMobile ? 'mobile' : 'desktop'}
              icon={
                <ExteriorIcon
                  fill={
                    galleryTab === 'gallery-exterior'
                      ? 'white '
                      : palette.primary.light
                  }
                  fontSize="small"
                />
              }
              iconPosition="start"
              label="exterior"
              sx={{
                background:
                  galleryTab === 'gallery-exterior'
                    ? 'rgba( 0, 0, 0, 0.65 )'
                    : '',
                color:
                  galleryTab === 'gallery-exterior'
                    ? 'white !important'
                    : 'rgba(255, 255, 255, 0.47) !important',
                flexDirection: isMobile && isPortrait ? 'column' : '',
              }}
              value="gallery-exterior"
              {...a11yProps(0)}
            />
          )}
          {interiorGalleryMid.length > 0 && (
            <CombinedTab
              device={isMobile ? 'mobile' : 'desktop'}
              icon={
                <InteriorIcon
                  fill={
                    galleryTab === 'gallery-interior'
                      ? 'white'
                      : palette.primary.light
                  }
                  fontSize="small"
                />
              }
              iconPosition="start"
              label="interior"
              sx={{
                background:
                  galleryTab === 'gallery-interior'
                    ? 'rgba( 0, 0, 0, 0.65 )'
                    : '',
                color:
                  galleryTab === 'gallery-interior'
                    ? 'white !important'
                    : 'rgba(255, 255, 255, 0.47) !important',
                flexDirection: isMobile && isPortrait ? 'column' : '',
              }}
              value="gallery-interior"
              {...a11yProps(1)}
            />
          )}
        </CombinedTabs>
      </Box>
      {galleryData.map((data: any, id: any) => (
        <CustomTabPanel currenttab={data.tab} gallerytab={galleryTab} key={id}>
          <ImageListItem
            gallery={data?.gallery}
            mountRef={mountRef}
            tab={data.tab}
          />
        </CustomTabPanel>
      ))}
    </Stack>
  );
}
